import React, { useContext, useEffect } from 'react';
import { UserContext } from '../contexts/UserContext';
import { useNavigate } from 'react-router-dom';
import { scrollPageToTop } from '../utils/scrollPageToTop';

// Public marketing page for the current giveaway (Chase Bliss Bad Mood,
// celebrating the iOS + Android app launches). Rebuilt 2026-09-21 from the
// Strymon Deco v2 page. Entry copy here is the PUBLIC simplification of the
// v2 rules in GIVEAWAY_PLAN.md (base entry = one listing; +1 per further
// listing added during the giveaway, up to the cap; +10 per completed trade).
// If the rules change there, change them here too.
//
// 2026-10-18 (Jason): the Wantlist step was dropped mid-giveaway so new
// members only have to list to enter. A loosening only; nobody who entered
// under the three-step version lost anything. Same day: a pedal listed during
// the giveaway keeps its entry after it is traded or sold here, but a listing
// that is switched off or removed without a trade does not count.
// 2026-10-20 (Jason): listings without photos earn nothing, and the copy now
// says photos must be of that pedal, so low-effort entries can be excluded.

// Palette convention (Jason, 2026-09-21): gold is for emphasis/highlights,
// blue (#4A9FCA) is reserved for ACTIONABLE text only (links, buttons).
// Nothing on this page is a text link, so blue is deliberately absent.
const GOLD = '#C9A227';
const SLATE = '#4A5759';
const CREAM = '#F7F3EB';

const Giveaway = () => {
  const { firestoreUser } = useContext(UserContext);
  const navigate = useNavigate();

  // The SPA has no router scroll restoration, so arriving from a scrolled
  // page (e.g. tapping the dashboard hero below the fold) lands mid-page
  // unless the destination scrolls itself (reported 2026-10-02).
  // scrollPageToTop returns its retry-cancelling cleanup; returning it from
  // the effect detaches the iOS reassert listener on unmount, per its docs.
  useEffect(() => scrollPageToTop({ blur: false }), []);

  const handleGetStarted = () => {
    if (firestoreUser) {
      navigate('/my-pedals/pedalboard');
    } else {
      navigate('/signup');
    }
  };

  const steps = [
    {
      number: '1',
      title: 'Create an account',
      detail: 'Free to join. Verify your email and phone so we know you’re real.',
    },
    {
      number: '2',
      title: 'List a pedal',
      detail: 'Add one pedal to your Pedalboard. That’s it, you’re entered.',
    },
  ];

  return (
    <div
      className="giveaway-page min-h-screen"
      style={{
        backgroundColor: CREAM,
        minHeight: 'calc(100vh - var(--navbar-height, 125px))',
        fontFamily: 'Nunito, sans-serif',
        paddingBottom: '4rem',
      }}
    >
      <style>{`
        .giveaway-hero-title {
          font-size: clamp(2rem, 5vw, 3rem);
        }
        .giveaway-section-title {
          font-size: clamp(1.5rem, 3.5vw, 2rem);
        }
        .giveaway-subtitle {
          font-size: clamp(1.125rem, 2.5vw, 1.375rem);
        }
        .giveaway-steps,
        .giveaway-boost-cards {
          display: flex;
          gap: 1.25rem;
          justify-content: center;
          flex-wrap: wrap;
        }
        .giveaway-step-card {
          flex: 1 1 220px;
          max-width: 300px;
        }
        .giveaway-boost-card {
          flex: 1 1 280px;
          max-width: 400px;
        }
        .giveaway-cta-button:hover {
          background-color: #B89020 !important;
          transform: scale(1.05);
        }
      `}</style>

      <div
        className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8"
        style={{ paddingTop: '3rem', width: '100%' }}
      >
        {/* ---- Hero ---- */}
        <h1
          className="giveaway-hero-title"
          style={{
            fontWeight: 'bold',
            color: GOLD,
            textAlign: 'center',
            marginBottom: '0.75rem',
            lineHeight: '1.2',
            padding: '0 24px',
          }}
        >
          Win This Chase Bliss Bad Mood!
        </h1>

        <p
          className="giveaway-subtitle"
          style={{
            fontWeight: '600',
            color: SLATE,
            textAlign: 'center',
            margin: '0 auto 2rem auto',
            maxWidth: '640px',
            lineHeight: '1.5',
            padding: '0 20px',
          }}
        >
          PedalxChange is now on iPhone and Android, and we&rsquo;re
          celebrating by giving one lucky member this limited-edition pedal.
        </p>

        <div
          style={{
            width: '100%',
            maxWidth: '760px',
            margin: '0 auto 3rem auto',
            padding: '0 20px',
            boxSizing: 'border-box',
          }}
        >
          <div
            style={{
              borderRadius: '15px',
              overflow: 'hidden',
              boxShadow: '0 10px 30px rgba(0, 0, 0, 0.15)',
            }}
          >
            <img
              src="/images/Giveaway_BM_2.png"
              alt="Chase Bliss Bad Mood giveaway"
              style={{ width: '100%', height: 'auto', display: 'block' }}
            />
          </div>
        </div>

        {/* ---- How to enter ---- */}
        <h2
          className="giveaway-section-title"
          style={{
            fontWeight: 'bold',
            color: GOLD,
            marginBottom: '0.5rem',
            textAlign: 'center',
          }}
        >
          How to Enter
        </h2>
        <p
          style={{
            fontSize: '1.125rem',
            color: SLATE,
            textAlign: 'center',
            marginBottom: '1.75rem',
            fontWeight: '500',
            padding: '0 24px',
          }}
        >
          Entry is free. Do all three and you&rsquo;re in the drawing:
        </p>

        <div className="giveaway-steps" style={{ marginBottom: '1.5rem' }}>
          {steps.map((step) => (
            <div
              key={step.number}
              className="giveaway-step-card"
              style={{
                backgroundColor: '#ffffff',
                borderRadius: '12px',
                padding: '1.5rem 1.25rem',
                textAlign: 'center',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.06)',
                border: '1px solid rgba(74, 87, 89, 0.08)',
              }}
            >
              <div
                style={{
                  width: '44px',
                  height: '44px',
                  borderRadius: '50%',
                  backgroundColor: GOLD,
                  color: '#ffffff',
                  fontSize: '1.375rem',
                  fontWeight: 'bold',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 0.875rem auto',
                }}
              >
                {step.number}
              </div>
              <div
                style={{
                  fontSize: '1.125rem',
                  fontWeight: '700',
                  color: SLATE,
                  marginBottom: '0.375rem',
                }}
              >
                {step.title}
              </div>
              <div style={{ fontSize: '0.95rem', color: SLATE, lineHeight: '1.5' }}>
                {step.detail}
              </div>
            </div>
          ))}
        </div>

        <p
          style={{
            fontSize: '1.0625rem',
            color: SLATE,
            textAlign: 'center',
            margin: '0 auto 3rem auto',
            maxWidth: '560px',
            fontWeight: '500',
            lineHeight: '1.6',
            padding: '0 24px',
          }}
        >
          <strong style={{ color: GOLD }}>Already a member?</strong> If you meet
          the entry criteria, you&rsquo;re automatically in. Nothing else to do.
        </p>

        {/* ---- Boost your chances ---- */}
        <h2
          className="giveaway-section-title"
          style={{
            fontWeight: 'bold',
            color: GOLD,
            marginBottom: '1.75rem',
            textAlign: 'center',
          }}
        >
          Boost Your Chances
        </h2>

        <div className="giveaway-boost-cards" style={{ marginBottom: '1.25rem' }}>
          <div
            className="giveaway-boost-card"
            style={{
              backgroundColor: '#ffffff',
              borderRadius: '12px',
              padding: '1.5rem',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.06)',
              border: '1px solid rgba(74, 87, 89, 0.08)',
              textAlign: 'center',
            }}
          >
            <div
              style={{
                fontSize: '2rem',
                fontWeight: '800',
                color: GOLD,
                marginBottom: '0.25rem',
              }}
            >
              +1 entry
            </div>
            <div
              style={{
                fontSize: '1.125rem',
                fontWeight: '700',
                color: SLATE,
                marginBottom: '0.5rem',
              }}
            >
              for every new pedal you list with its own photos
            </div>
            <div style={{ fontSize: '0.95rem', color: SLATE, lineHeight: '1.55' }}>
              Each new pedal added to your Pedalboard during the giveaway,
              with genuine photos of that pedal, earns another entry. Trade or
              sell it here and you keep the entry. Listings without photos, or
              with photos that are not of that pedal, do not count.
            </div>
          </div>

          <div
            className="giveaway-boost-card"
            style={{
              backgroundColor: '#ffffff',
              borderRadius: '12px',
              padding: '1.5rem',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.06)',
              border: '1px solid rgba(74, 87, 89, 0.08)',
              textAlign: 'center',
            }}
          >
            <div
              style={{
                fontSize: '2rem',
                fontWeight: '800',
                color: GOLD,
                marginBottom: '0.25rem',
              }}
            >
              +10 entries
            </div>
            <div
              style={{
                fontSize: '1.125rem',
                fontWeight: '700',
                color: SLATE,
                marginBottom: '0.5rem',
              }}
            >
              for every trade you complete
            </div>
            <div style={{ fontSize: '0.95rem', color: SLATE, lineHeight: '1.55' }}>
              Finish a trade during the giveaway and both traders earn ten
              entries each.
            </div>
          </div>
        </div>

        <p
          style={{
            fontSize: '0.95rem',
            color: SLATE,
            textAlign: 'center',
            margin: '0 auto 3rem auto',
            maxWidth: '600px',
            lineHeight: '1.6',
            opacity: 0.85,
            padding: '0 24px',
            fontWeight: '700',
          }}
        >
          Play fair: every listing is scanned and verified. Fake, spam, or
          low-effort listings (no photos, or one photo reused across many
          pedals) don&rsquo;t count, and can disqualify your account.
          Entries are counted on the draw date, October 31st.
        </p>

        {/* ---- Date + CTA ---- */}
        <div
          style={{
            backgroundColor: 'rgba(201, 162, 39, 0.1)',
            border: `1px solid ${GOLD}`,
            borderRadius: '12px',
            maxWidth: '560px',
            margin: '0 auto 2.5rem auto',
            padding: '1.25rem 1.5rem',
            textAlign: 'center',
          }}
        >
          <div
            style={{
              fontSize: '1.25rem',
              fontWeight: '700',
              color: GOLD,
              lineHeight: '1.4',
            }}
          >
            🎃 The winner will be announced on Halloween, October 31st, 2026
          </div>
        </div>

        <div style={{ textAlign: 'center', marginBottom: '4rem' }}>
          <button
            onClick={handleGetStarted}
            className="giveaway-cta-button"
            style={{
              fontFamily: 'Nunito, sans-serif',
              fontSize: '1.25rem',
              fontWeight: 'bold',
              color: 'white',
              backgroundColor: GOLD,
              border: 'none',
              borderRadius: '50px',
              padding: '1rem 3rem',
              cursor: 'pointer',
              boxShadow: '0 4px 12px rgba(201, 162, 39, 0.3)',
              transition: 'all 0.3s ease',
            }}
          >
            {firestoreUser ? 'Go to My Pedalboard' : 'Get Started Now'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default Giveaway;
